/**
 * Tabs - 탭 내비게이션 컴포넌트
 *
 * @param variant - 'pill' | 'underline' | 'bordered' 스타일 변형
 * @param orientation - 'horizontal' | 'vertical' 배치 방향
 * @param badgeCounts - 탭별 뱃지 숫자 (Record<탭 value, 숫자>)
 */
'use client';

import React, { useCallback, useRef } from 'react';
import styles from './Tabs.module.css';

export interface Tab {
  label: string;
  value: string;
}

export interface TabsProps {
  tabs: Tab[];
  activeTab: string;
  onChange: (value: string) => void;
  variant?: 'pill' | 'underline' | 'bordered';
  orientation?: 'horizontal' | 'vertical';
  badgeCounts?: Record<string, number>;
}

export default function Tabs({
  tabs,
  activeTab,
  onChange,
  variant = 'pill',
  orientation = 'horizontal',
  badgeCounts,
}: TabsProps) {
  const tablistRef = useRef<HTMLDivElement>(null);

  const isVertical = orientation === 'vertical';

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      const currentIndex = tabs.findIndex((t) => t.value === activeTab);
      let nextIndex: number | null = null;

      const nextKey = isVertical ? 'ArrowDown' : 'ArrowRight';
      const prevKey = isVertical ? 'ArrowUp' : 'ArrowLeft';

      switch (e.key) {
        case nextKey:
          nextIndex = (currentIndex + 1) % tabs.length;
          break;
        case prevKey:
          nextIndex = (currentIndex - 1 + tabs.length) % tabs.length;
          break;
        case 'Home':
          nextIndex = 0;
          break;
        case 'End':
          nextIndex = tabs.length - 1;
          break;
        default:
          return;
      }

      e.preventDefault();
      onChange(tabs[nextIndex].value);

      const buttons = tablistRef.current?.querySelectorAll<HTMLButtonElement>('[role="tab"]');
      if (buttons && buttons[nextIndex]) {
        buttons[nextIndex].focus();
      }
    },
    [tabs, activeTab, onChange, isVertical],
  );

  const containerCls = [styles.tabs, styles[variant], isVertical && styles.vertical]
    .filter(Boolean)
    .join(' ');

  return (
    <div
      ref={tablistRef}
      className={containerCls}
      role="tablist"
      aria-orientation={orientation}
      onKeyDown={handleKeyDown}
    >
      {tabs.map((tab) => {
        const isActive = activeTab === tab.value;
        const count = badgeCounts?.[tab.value];
        return (
          <button
            key={tab.value}
            className={`${styles.tabButton} ${isActive ? styles.active : ''}`}
            role="tab"
            aria-selected={isActive}
            tabIndex={isActive ? 0 : -1}
            id={`tab-${tab.value}`}
            aria-controls={`tabpanel-${tab.value}`}
            onClick={() => onChange(tab.value)}
          >
            {tab.label}
            {count !== undefined && count > 0 && <span className={styles.badgeCount}>{count}</span>}
          </button>
        );
      })}
    </div>
  );
}
